import { create } from 'zustand'
import type { UserProfile } from '../types/models.js'
import * as authApi from '../api/auth.js'
import { ApiError } from '../types/api.js'
import { useOrgStore } from './org.store.js'

export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated'
export type ProfileStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface RegisterData {
  email: string
  password: string
  firstName?: string
  lastName?: string
}

interface AuthState {
  status: AuthStatus
  user: UserProfile | null
  accessToken: string | null
  refreshToken: string | null
  error: string | null
  profileStatus: ProfileStatus
  profileError: string | null
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  bootstrap: () => Promise<void>
  refreshProfile: () => Promise<void>
  clear: () => void
}

export type AuthStoreState = AuthState & AuthActions

const ACCESS_KEY = 'mf_access'
const REFRESH_KEY = 'mf_refresh'
const USER_KEY = 'mf_user'

interface CachedSession {
  accessToken: string | null
  refreshToken: string | null
  user: UserProfile | null
}

function readSession(): CachedSession {
  const accessToken = localStorage.getItem(ACCESS_KEY)
  const refreshToken = sessionStorage.getItem(REFRESH_KEY)
  let user: UserProfile | null = null
  try {
    const raw = localStorage.getItem(USER_KEY)
    if (raw) user = JSON.parse(raw) as UserProfile
  } catch {
    user = null
  }
  return { accessToken, refreshToken, user }
}

function persistSession(accessToken: string, refreshToken: string | null, user: UserProfile): void {
  localStorage.setItem(ACCESS_KEY, accessToken)
  if (refreshToken) sessionStorage.setItem(REFRESH_KEY, refreshToken)
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

function clearSession(): void {
  localStorage.removeItem(ACCESS_KEY)
  sessionStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(USER_KEY)
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error) return e.message
  return 'Network error'
}

const UNAUTHENTICATED: AuthState = {
  status: 'unauthenticated',
  user: null,
  accessToken: null,
  refreshToken: null,
  error: null,
  profileStatus: 'idle',
  profileError: null,
}

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  status: 'initializing',
  user: null,
  accessToken: null,
  refreshToken: null,
  error: null,
  profileStatus: 'idle',
  profileError: null,

  async login(email: string, password: string) {
    set({ error: null })
    try {
      const result = await authApi.login(email, password)
      persistSession(result.tokens.accessToken, result.tokens.refreshToken, result.user)
      set({
        status: 'authenticated',
        user: result.user,
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
        error: null,
        profileStatus: 'ready',
        profileError: null,
      })
    } catch (e: unknown) {
      set({ error: errorMessage(e) })
      throw e
    }
  },

  async register(data: RegisterData) {
    set({ error: null })
    try {
      const result = await authApi.register(data)
      persistSession(result.tokens.accessToken, result.tokens.refreshToken, result.user)
      set({
        status: 'authenticated',
        user: result.user,
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
        error: null,
        profileStatus: 'ready',
        profileError: null,
      })
    } catch (e: unknown) {
      set({ error: errorMessage(e) })
      throw e
    }
  },

  async logout() {
    try {
      await authApi.logout()
    } catch {
      // ignore server error on logout
    }
    get().clear()
  },

  async bootstrap() {
    const cached = readSession()

    if (!cached.accessToken && !cached.refreshToken) {
      set({ ...UNAUTHENTICATED })
      return
    }

    if (cached.accessToken) {
      set({
        accessToken: cached.accessToken,
        user: cached.user,
        error: null,
        profileStatus: 'loading',
        profileError: null,
      })
      try {
        const profile = await authApi.loadMe()
        persistSession(cached.accessToken, cached.refreshToken, profile)
        set({
          status: 'authenticated',
          user: profile,
          accessToken: cached.accessToken,
          refreshToken: cached.refreshToken,
          error: null,
          profileStatus: 'ready',
          profileError: null,
        })
        return
      } catch (e: unknown) {
        const isUnauthorized = e instanceof ApiError && e.status === 401
        if (!isUnauthorized && cached.user) {
          // Transient failure (network/5xx): keep optimistic session, but mark
          // the profile as unverified so guarded platform surfaces can offer a
          // retry instead of trusting a stale platform role.
          set({
            status: 'authenticated',
            user: cached.user,
            accessToken: cached.accessToken,
            profileStatus: 'error',
            profileError: errorMessage(e),
          })
          return
        }
      }
    }

    if (cached.refreshToken) {
      try {
        const tokens = await authApi.refresh(cached.refreshToken)
        localStorage.setItem(ACCESS_KEY, tokens.accessToken)
        sessionStorage.setItem(REFRESH_KEY, tokens.refreshToken)
        const profile = await authApi.loadMe()
        persistSession(tokens.accessToken, tokens.refreshToken, profile)
        set({
          status: 'authenticated',
          user: profile,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          error: null,
          profileStatus: 'ready',
          profileError: null,
        })
        return
      } catch {
        // fall through to unauthenticated
      }
    }

    if (get().status === 'initializing') {
      clearSession()
      set({ ...UNAUTHENTICATED })
    }
  },

  async refreshProfile() {
    const accessToken = get().accessToken
    if (!accessToken) {
      set({ ...UNAUTHENTICATED })
      return
    }

    set({ profileStatus: 'loading', profileError: null })
    try {
      const profile = await authApi.loadMe()
      const current = get()
      persistSession(accessToken, current.refreshToken, profile)
      set({
        status: 'authenticated',
        user: profile,
        accessToken,
        profileStatus: 'ready',
        profileError: null,
      })
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 401) {
        get().clear()
        return
      }
      set({
        profileStatus: 'error',
        profileError: errorMessage(e),
      })
    }
  },

  clear() {
    clearSession()
    useOrgStore.getState().clear()
    set({ ...UNAUTHENTICATED })
  },
}))

/**
 * Imperative facade for non-React callers (module bootstrap, guards, tests).
 * React components must use `useAuthStore` so they re-render on state changes.
 */
export const authStore = {
  bootstrap: (): Promise<void> => useAuthStore.getState().bootstrap(),
  login: (email: string, password: string): Promise<void> => useAuthStore.getState().login(email, password),
  register: (data: RegisterData): Promise<void> => useAuthStore.getState().register(data),
  logout: (): Promise<void> => useAuthStore.getState().logout(),
  refreshProfile: (): Promise<void> => useAuthStore.getState().refreshProfile(),
  clear: (): void => useAuthStore.getState().clear(),
}
